import React, { useMemo, useState, useCallback } from 'react';
import AltRouteRoundedIcon from '@mui/icons-material/AltRouteRounded';
import { Box, Chip, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material';
import type { SurveyDestination } from '@apruv/survey-contract/compiler';
import type { SurveyDocument } from '@apruv/survey-contract/types';
import CanonicalSurveyRenderer from '../presentation/CanonicalSurveyRenderer';
import { canonicalSurveyContext } from '../presentation/canonicalSurveyContext';
import { canonicalSurveyToFlow } from '../presentation/canonicalSurveyFlow';
import { createCanonicalFlowDriver } from '../presentation/canonicalFlowDriver';
import {
  publicSurveyInitialValues,
  publicSurveySubmission,
} from '../../components/organisms/PublicCXSurveyWrapper';
import type { FlowValues, FlowActionEvent } from '../types';
import { prepareCanonicalPreview, type CanonicalPreviewScenario } from './prepareCanonicalPreview';

export function CanonicalPreview({
  payload,
  onAction,
  onComplete,
  onDestination: reportDestination,
}: {
  payload: Record<string, unknown>;
  onAction: (action: FlowActionEvent) => void;
  onComplete: (values: FlowValues) => void;
  onDestination?: (destination: SurveyDestination) => void;
}) {
  const [visits, setVisits] = useState<SurveyDestination[]>([]);
  const [detailsAnchor, setDetailsAnchor] = useState<HTMLElement | null>(null);
  const onDestination = useCallback(
    (destination: SurveyDestination) => {
      setVisits((current) =>
        JSON.stringify(current[current.length - 1]) === JSON.stringify(destination)
          ? current
          : [...current.slice(-99), destination]
      );
      reportDestination?.(destination);
    },
    [reportDestination]
  );
  const prepared = useMemo(() => {
    const result = prepareCanonicalPreview(
      payload.document as SurveyDocument,
      payload.scenario as CanonicalPreviewScenario,
      payload.initialScreenId as string | undefined
    );
    const driver = createCanonicalFlowDriver(result.compiled, result.response);
    const flow = canonicalSurveyToFlow(
      result.compiled,
      result.response,
      canonicalSurveyContext(result.compiled, result.response)
    );
    const initialValues = publicSurveyInitialValues(flow, result.response, 'preview', {
      componentId: driver.initialRatingComponentId,
    });
    const allowedIds = new Set(
      result.compiled.snapshot.screens.flatMap((screen) =>
        screen.components.map((component) => component.id)
      )
    );
    for (const [key, value] of Object.entries((payload.initialValues ?? {}) as FlowValues)) {
      if (allowedIds.has(key)) initialValues[key] = value;
    }
    return { ...result, initialValues, flow };
  }, [payload]);
  const document = prepared.compiled.snapshot;
  const destinationLabel = (destination: { kind: string; screenId?: string }) =>
    destination.kind === 'screen'
      ? document.screens.find((item) => item.id === destination.screenId)?.name ||
        destination.screenId
      : 'Complete';
  const flags = prepared.flagSnapshot;
  const current = visits[visits.length - 1];
  const node =
    current?.kind === 'screen'
      ? document.graph.nodes.find((item) => item.id === current.nodeId)
      : undefined;
  const routes = node ? document.graph.edges.filter((edge) => edge.sourceNodeId === node.id) : [];
  const targetLabel = (kind: string, id: string) => {
    if (kind === 'stage') {
      const stage = document.graph.nodes.find((item) => item.id === id);
      return stage?.screenId
        ? destinationLabel({ kind: 'screen', screenId: stage.screenId })
        : stage?.title || id;
    }
    if (kind === 'route') return document.graph.edges.find((item) => item.id === id)?.label || id;
    if (kind === 'action') return document.actions.find((item) => item.id === id)?.label || id;
    return (
      document.screens.flatMap((screen) => screen.components).find((item) => item.id === id)
        ?.type || id
    );
  };
  const skipped = document.flags.guards.filter(
    (guard) => flags.resolvedFlags[guard.flagId] !== guard.equals
  );
  const embedded = new URLSearchParams(window.location.search).get('embedded') === '1';
  return (
    <>
      {!embedded && (
        <Box sx={{ position: 'fixed', top: 8, right: 8, zIndex: 1200 }}>
          <Tooltip title="Simulation details" placement="left" arrow>
            <IconButton
              aria-label="Open simulation details"
              size="small"
              onClick={(event) => setDetailsAnchor(event.currentTarget)}
              sx={{
                width: 32,
                height: 32,
                color: 'text.secondary',
                bgcolor: 'rgba(255, 255, 255, 0.94)',
                border: 1,
                borderColor: 'divider',
                boxShadow: 2,
                backdropFilter: 'blur(8px)',
                '&:hover': { bgcolor: 'background.paper', color: 'text.primary' },
              }}
            >
              <AltRouteRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {!embedded && (
        <Popover
          open={Boolean(detailsAnchor)}
          anchorEl={detailsAnchor}
          onClose={() => setDetailsAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disableScrollLock
          slotProps={{
            paper: {
              'aria-label': 'Simulation path',
              sx: {
                width: 'min(320px, calc(100vw - 24px))',
                maxHeight: 'min(520px, calc(100vh - 24px))',
                mt: 0.75,
                p: 1.5,
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                boxShadow: 8,
              },
            },
          }}
        >
          <Stack spacing={1}>
          <Box>
            <Typography variant="subtitle2">Simulation path</Typography>
            <Typography variant="caption">
              Version {document.identity.experienceVersionId} · Revision{' '}
              {document.identity.draftRevision}
            </Typography>
          </Box>
          <Typography variant="caption">
            Tipping eligibility:{' '}
            {(payload.scenario as CanonicalPreviewScenario).tippingEligible
              ? 'Simulated eligible'
              : 'Not eligible'}
            . Flags do not grant eligibility.
          </Typography>
          {document.flags.bindings.map((binding) => {
            const flag = document.flags.definitions.find((item) => item.id === binding.flagId)!;
            const source = flags.provenance[binding.flagId];
            const requested = Object.prototype.hasOwnProperty.call(
              flags.requestedFlags,
              binding.flagId
            )
              ? flags.requestedFlags[binding.flagId]
                ? 'On'
                : 'Off'
              : 'Not supplied';
            return (
              <Typography key={binding.flagId} variant="caption">
                {flag.name}: requested {requested}; resolved{' '}
                {flags.resolvedFlags[binding.flagId] ? 'On' : 'Off'}; source{' '}
                {
                  {
                    default: 'Configured default',
                    event: 'Event input',
                    experience_on: 'Experience On',
                    experience_off: 'Experience Off',
                  }[source.source]
                }
              </Typography>
            );
          })}
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
            {visits.map((destination, index) => (
              <Chip
                key={index}
                label={destinationLabel(destination)}
                color={index === visits.length - 1 ? 'primary' : 'default'}
                size="small"
              />
            ))}
          </Stack>
          {routes.length > 0 && (
            <Typography variant="caption">
              Possible next routes (chosen after input):{' '}
              {routes
                .map((edge) => {
                  const target = document.graph.nodes.find((item) => item.id === edge.targetNodeId);
                  return `${edge.label || 'Continue'} → ${target?.screenId ? destinationLabel({ kind: 'screen', screenId: target.screenId }) : target?.title || 'Complete'}`;
                })
                .join(' · ')}
            </Typography>
          )}
          {skipped.length > 0 && (
            <Typography variant="caption">Disabled targets in this scenario</Typography>
          )}
          {skipped.map((guard, index) => (
            <Typography key={index} variant="caption">
              {targetLabel(guard.target.kind, guard.target.id)}: disabled by{' '}
              {document.flags.definitions.find((item) => item.id === guard.flagId)?.name ||
                guard.flagId}
              ; bypass → {destinationLabel(guard.disabledDestination)}
            </Typography>
          ))}
          </Stack>
        </Popover>
      )}
      <CanonicalSurveyRenderer
        onPreviewDestination={onDestination}
        compiled={prepared.compiled}
        response={prepared.response}
        initialValues={prepared.initialValues}
        onAction={onAction}
        onComplete={onComplete}
        beforeNavigate={async (values, owner) =>
          canonicalPreviewAcknowledgements(prepared.flow, values, owner)
        }
      />
    </>
  );
}

/** Same eligibility/ownership as the public save path, with no service call. */
export function canonicalPreviewAcknowledgements(
  flow: import('../types').FlowConfig,
  values: FlowValues,
  owner?: string
): string[] {
  if (!publicSurveySubmission(flow, values, { componentId: owner })) return [];
  return flow.screens
    .flatMap((screen) => screen.components)
    .filter(
      (component) =>
        (component.id === owner || component.type === 'feedbackForm') &&
        values[component.id] != null
    )
    .map((component) => component.id);
}
